import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuizScreen from '../screens/QuizScreen.jsx';
import SuccessScreen from '../screens/SuccessScreen.jsx';
import RewardModal from '../components/RewardModal.jsx';
import Toast from '../components/Toast.jsx';
import SiteHeader from '../components/SiteHeader.jsx';
import RecommendedQuizzes from '../components/RecommendedQuizzes.jsx';
import QuizContent from '../components/QuizContent.jsx';
import { useAds } from '../context/AdContext.jsx';
import { useCoins } from '../hooks/useCoins.jsx';
import { getQuizQuestions, advanceRotation } from '../data/questions.js';
import { CATEGORIES } from '../data/categories.js';
import { INTERSTITIAL_EVERY } from '../ads/adConfig.js';
import { useSeo } from '../hooks/useSeo.js';

// The quiz game flow, extracted from the old App.jsx state machine.
// Behavior (ad triggers, reward doubling, interstitial cadence) is unchanged.
export default function PlayPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { showInterstitial, showRewarded } = useAds();
  const { coins, addCoins } = useCoins();

  const category = CATEGORIES.find((c) => c.id === params.get('cat'));
  const catId = params.get('cat') || 'default';

  useSeo(
    category ? `${category.title} Quiz — QuizzyLoot` : 'Play a Quiz — QuizzyLoot',
    category
      ? `Answer ${category.title} trivia questions on QuizzyLoot and earn coins. Free to play.`
      : 'Play free trivia quizzes on QuizzyLoot and earn coins.',
  );

  // This round's rotating question set (fresh each play of the same quiz).
  const [questions, setQuestions] = useState(() => getQuizQuestions(catId));

  const [screen, setScreen] = useState('quiz'); // 'quiz' -> 'success'
  const [index, setIndex] = useState(0);
  const [earned, setEarned] = useState(0); // coins won in the current round
  const [correct, setCorrect] = useState(0);
  const [locked, setLocked] = useState(false);
  const [reward, setReward] = useState(null); // { amount } when modal open
  const [toast, setToast] = useState(null);

  // Bump the rotation pointer once so the NEXT play of this quiz differs.
  const bumped = useRef(false);
  useEffect(() => {
    if (bumped.current) return;
    bumped.current = true;
    advanceRotation(catId);
  }, [catId]);

  const showToast = (msg = 'Asset Credited') => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Advance within the quiz. Fire an interstitial only on the cadence at a
  // natural mid-round break — never right before the results screen.
  const advance = async (nextIndex) => {
    const isLast = nextIndex >= questions.length;
    const shouldFire = !isLast && nextIndex % INTERSTITIAL_EVERY === 0;
    if (shouldFire) {
      setLocked(true);
      await showInterstitial();
    }
    if (isLast) {
      setScreen('success');
    } else {
      setIndex(nextIndex);
    }
    setLocked(false);
  };

  const handleAnswer = (_picked, isCorrect) => {
    if (isCorrect) {
      const amount = questions[index].coins;
      addCoins(amount);
      setEarned((e) => e + amount);
      setCorrect((c) => c + 1);
      setTimeout(() => setReward({ amount }), 650);
    } else {
      setTimeout(() => advance(index + 1), 1100);
    }
  };

  const handleClaim = async () => {
    const amount = reward.amount;
    setReward(null);
    setLocked(true);
    const { viewed } = await showRewarded();
    if (viewed) {
      addCoins(amount); // double the just-won coins
      setEarned((e) => e + amount);
      showToast();
    }
    setLocked(false);
    advance(index + 1);
  };

  const handleClose = () => {
    setReward(null);
    advance(index + 1);
  };

  // Start a fresh round on the same category — with a new rotated question set.
  const playAgain = () => {
    setQuestions(getQuizQuestions(catId));
    advanceRotation(catId);
    setIndex(0);
    setEarned(0);
    setCorrect(0);
    setReward(null);
    setLocked(false);
    setScreen('quiz');
  };

  return (
    <div className="min-h-screen flex flex-col page-in">
      <SiteHeader variant="light" />

      <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-8 items-start">
          {/* Quiz / results column */}
          <div className="pt-2">
            {screen === 'quiz' && (
              <QuizScreen
                index={index}
                questions={questions}
                coins={coins}
                locked={locked}
                categoryTitle={category?.title}
                onAnswer={handleAnswer}
              />
            )}

            {screen === 'success' && (
              <SuccessScreen
                coins={coins}
                earned={earned}
                correct={correct}
                total={questions.length}
                ctaLabel="Back to Home"
                onContinue={() => navigate('/')}
                onPlayAgain={playAgain}
              />
            )}
          </div>

          {/* Recommended quizzes (YouTube-style "up next") */}
          <RecommendedQuizzes currentId={catId} />
        </div>

        {/* Informational content (SEO / AdSense-safe) */}
        <QuizContent category={category} />
      </main>

      {reward && (
        <RewardModal
          coins={reward.amount}
          onClaim={handleClaim}
          onClose={handleClose}
        />
      )}

      <Toast show={!!toast} message={toast} />
    </div>
  );
}
